import React, { useState, useEffect } from "react";
import Axios from "axios";

import {
    getCurrentVersionSelection,
    subscribeToVersionSelection,
} from "../utils/vehicleVersionBus";

Axios.defaults.headers.common["X-CSRFTOKEN"] = document.querySelector('meta[name="csrf-token"]').content;

const DEFAULT_ANNUAL_KM = 15000;

const SavingsCalculatorContext = React.createContext([{}, () => { }]);

// Los precios y los km por defecto llegan resueltos en el HTML (`json_script`),
// así que el único fetch del bootstrap es el listado de versiones.
const buildInitialState = (config) => {
    const annualKm = config.defaultAnnualKm || DEFAULT_ANNUAL_KM;
    return {
        loading: true,
        versions: [],
        // Precios de referencia por combustible, ya resueltos por el backend.
        fuelPrices: config.fuelPrices || {},
        defaultAnnualKm: annualKm,
        // Selección de modelos Toyota (máx 4). En modo `single` se precarga uno.
        selectedVersionIds: [],
        // Versión de la tarjeta precargada (modo `single`). Arranca con lo que
        // definió el bloque y luego la manda el selector de versiones de la ficha.
        precharged: {
            versionId: parseInt(config.preselectedVersionId, 10) || null,
            name: config.preselectedName || "",
            motor: config.preselectedMotor || "",
            image: config.preselectedImage || "",
        },
        // Datos del vehículo actual del usuario.
        currentVehicle: {
            fuelType: "GASOLINE",
            efficiency: "",
            annualKm,
        },
        // Resultados por versión: { [id]: { result, loading, error } }
        results: {},
    };
};

const SavingsCalculatorProvider = ({ config, children }) => {
    const [state, setState] = useState(() => buildInitialState(config));
    const isPrecharged = config.mode === "single";
    const prechargedVersionId = state.precharged.versionId;

    useEffect(() => {
        Axios.get(config.versionsUrl)
            .then((versionsResponse) => {
                setState((prev) => ({ ...prev, versions: versionsResponse.data || [] }));
            })
            .catch((error) => {
                console.error("[savings-calculator] bootstrap failed", error);
            })
            .finally(() => {
                setState((prev) => ({ ...prev, loading: false }));
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // En la ficha de modelo la calculadora sigue la versión que el usuario elige
    // en el selector de la página (mismo comportamiento que el resto del contenido).
    useEffect(() => {
        if (!isPrecharged) return undefined;

        const apply = (selection) => {
            if (!selection || !selection.id) return;
            setState((prev) => ({
                ...prev,
                precharged: {
                    versionId: parseInt(selection.id, 10),
                    name: selection.name || "",
                    motor: selection.motor || "",
                    image: selection.image || "",
                },
            }));
        };

        // El selector puede haber elegido versión antes de que montemos.
        apply(getCurrentVersionSelection());
        return subscribeToVersionSelection(apply);
    }, [isPrecharged]);

    // La selección efectiva se aplica cuando el listado ya está cargado: si la
    // versión no tiene datos energéticos, queda sin seleccionar y el paso 1
    // muestra el estado "Modelo no disponible".
    useEffect(() => {
        if (!isPrecharged) return;

        const available = state.versions.some((v) => v.id === prechargedVersionId);
        const next = available ? [prechargedVersionId] : [];

        setState((prev) => {
            const current = prev.selectedVersionIds;
            if (current.length === next.length && current[0] === next[0]) return prev;
            return { ...prev, selectedVersionIds: next, results: {} };
        });
    }, [isPrecharged, state.versions, prechargedVersionId]);

    return (
        <SavingsCalculatorContext.Provider value={[state, setState, config]}>
            {children}
        </SavingsCalculatorContext.Provider>
    );
};

export { SavingsCalculatorContext, SavingsCalculatorProvider };
